import { MinMax, stateContextType } from "@/contexts/context.state";
import { storeContextType } from "@/contexts/context.store";
import { Action, CollectAction, CollectUpdateByDelta, UpdateByDelta } from "@/events/events";
import { Task } from "@lit/task";
import { ProductBundleOption, ProductOption } from "@repobit/dex-store";
import { ReactiveController, ReactiveControllerHost } from "lit";

type Options = Map<symbol | string, Promise<ProductOption[]>>;
type Actions = Map<symbol | string, NonNullable<CollectAction["action"]>>;
type DeltaUpdate = Map<symbol | string, NonNullable<CollectUpdateByDelta["update"]>>;
type OptionMap = Map<string, ProductOption>;

type ComputeHost = ReactiveControllerHost & {
  storeName            : symbol | string;
  _store?              : storeContextType;
  _options             : Options;
  _actions             : Actions;
  _deltaUpdates        : DeltaUpdate;
  _partialOptions      : Actions;
  _partialBundleOptions: Actions;
  state                : stateContextType;
};

export class ComputeController implements ReactiveController {
  private host        : ComputeHost;
  private _computeTask: Task<unknown[], ProductOption[] | undefined>;

  constructor(host: ComputeHost) {
    this.host = host;
    host.addController(this);

    this._computeTask = new Task(host, {
      task: async (_, { signal }) => {
        const isActive = () => {
          if (signal.aborted) {
            throw new DOMException("Task aborted", `AbortError ${this.host.storeName.toString()}`);
          }
        };

        try {
          const computed = await this._computeState(isActive);
          await this._computeContext(computed, isActive);
          return computed;
        } catch (error: unknown) {
          if (this._isAbortError(error)) {
            return [];
          }
          console.error(error);
          return [];
        }
      },
      args: () => [this.host._store]
    });
  }

  get value() {
    return this._computeTask.value;
  }

  get taskComplete() {
    return this._computeTask.taskComplete;
  }

  run() {
    this._computeTask.run();
  }

  abort() {
    this._computeTask.abort();
  }

  hostConnected() {
    // no-op
  }

  hostDisconnected() {
    // no-op
  }

  hostUpdate() {
    // no-op
  }

  hostUpdated() {
    // no-op
  }

  private _isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name.includes("AbortError");
  }

  private async _computeState(isActive: () => void): Promise<ProductOption[]> {
    const computed: OptionMap = new Map();
    const queue: ProductOption[] = [];

    const enqueue = (option: ProductOption | null | undefined) => {
      if (!option) return;
      const key = this._getOptionKey(option);
      if (computed.has(key)) return;
      computed.set(key, option);
      queue.push(option);
    };

    await this._seedOptions(enqueue, isActive);
    await this._expandFixedPoint(queue, enqueue, isActive);
    await this._applyBundleCombinations(computed, enqueue, isActive);

    return [...computed.values()];
  }

  private async _seedOptions(
    enqueue: (option: ProductOption | null | undefined) => void,
    isActive: () => void
  ) {
    for (const optionsPromise of this.host._options.values()) {
      isActive();
      const options = await optionsPromise;
      for (const option of options) {
        isActive();
        enqueue(option);
        const partials = await this._applyPartials(option, isActive);
        for (const partial of partials) {
          enqueue(partial);
        }
      }
    }
  }

  private async _expandFixedPoint(
    queue: ProductOption[],
    enqueue: (option: ProductOption | null | undefined) => void,
    isActive: () => void
  ) {
    while (queue.length > 0) {
      isActive();
      const current = queue.shift()!;

      const actionResults = await this._applyActions(current, isActive);
      for (const option of actionResults) {
        enqueue(option);
      }

      const deltaResults = await this._applyDeltaUpdates(current, isActive);
      for (const option of deltaResults) {
        enqueue(option);
      }
    }
  }

  private async _applyBundleCombinations(
    computed: OptionMap,
    enqueue: (option: ProductOption | null | undefined) => void,
    isActive: () => void
  ) {
    const bundles = [...this.host._partialBundleOptions.values()];
    for (const option of [...computed.values()]) {
      isActive();
      const bundleResults = await this._applyBundles(option, bundles);
      for (const bundled of bundleResults) {
        enqueue(bundled);
      }
    }
  }

  private _getOptionKey(option: ProductOption): string {
    const product = option.getProduct();
    const bundle = option.getBundle()
      .map(({ option: bundled }) => this._getOptionKey(bundled))
      .sort()
      .join("|");
    return [
      product.getId(),
      product.getCampaign() || "",
      option.getDevices(),
      option.getSubscription(),
      bundle
    ].join("::");
  }

  private async _applyPartials(
    baseOpt: ProductOption,
    isActive: () => void
  ): Promise<ProductOption[]> {
    const results: ProductOption[] = [];
    for (const partial of this.host._partialOptions.values()) {
      isActive();
      if (!partial.id) {
        continue;
      }
      const newProduct = await baseOpt.switchProduct({ id: partial.id, campaign: partial.campaign });
      const newOpt = await newProduct?.getOption({
        devices     : partial.devices ?? baseOpt.getDevices(),
        subscription: partial.subscription ?? baseOpt.getSubscription()
      });
      if (newOpt) results.push(newOpt);
    }
    return results;
  }

  private async _applyActions(
    baseOpt: ProductOption,
    isActive: () => void
  ): Promise<ProductOption[]> {
    const results: ProductOption[] = [];
    for (const action of this.host._actions.values()) {
      isActive();
      const targetDevices = action.devices ?? baseOpt.getDevices();
      const targetSubscription = action.subscription ?? baseOpt.getSubscription();
      // Skip no-op actions that don't change anything
      if (targetDevices === baseOpt.getDevices() && targetSubscription === baseOpt.getSubscription()) {
        continue;
      }
      const newOpt = await baseOpt.getOption({
        devices     : targetDevices,
        subscription: targetSubscription
      });
      if (newOpt) results.push(newOpt);
    }
    return results;
  }

  private async _applyDeltaUpdates(
    baseOpt: ProductOption,
    isActive: () => void
  ): Promise<ProductOption[]> {
    const results: ProductOption[] = [];

    for (const action of this.host._deltaUpdates.values()) {
      isActive();
      const updates = action.useAsValue
        ? await this._applyAbsoluteDelta(baseOpt, action, isActive)
        : await this._applySteppedDelta(baseOpt, action, isActive);

      for (const option of updates) {
        results.push(option);
      }
    }

    return results;
  }

  private async _applyAbsoluteDelta(
    baseOpt: ProductOption,
    action: UpdateByDelta,
    isActive: () => void
  ): Promise<ProductOption[]> {
    const values = this._getDimensionValues(baseOpt, action.type);
    const results: ProductOption[] = [];
    const bounds = this._getAbsoluteBounds(action, action.delta);

    for (const value of values) {
      isActive();
      if (value < bounds.min || value > bounds.max) continue;
      const option = await this._getOptionByDimension(baseOpt, action.type, value);
      if (option) results.push(option);
    }

    return results;
  }

  private async _applySteppedDelta(
    baseOpt: ProductOption,
    action: UpdateByDelta,
    isActive: () => void
  ): Promise<ProductOption[]> {
    const results: ProductOption[] = [];
    const values = this._getDimensionValues(baseOpt, action.type);
    let current = action.type === "devices"
      ? baseOpt.getDevices()
      : baseOpt.getSubscription();

    while (true) {
      isActive();
      const next = this._computeDelta(values, current, action);
      if (next === null) break;

      current = next;
      const option = await this._getOptionByDimension(baseOpt, action.type, current);
      if (option) {
        results.push(option);
      }
    }

    return results;
  }

  private _getDimensionValues(
    option: ProductOption,
    type: UpdateByDelta["type"]
  ): number[] {
    const product = option.getProduct();
    return type === "devices"
      ? product.getDevices().values
      : product.getSubscriptions().values;
  }

  private _getAbsoluteBounds(
    action: UpdateByDelta,
    fallback: UpdateByDelta["delta"]
  ): { min: number; max: number } {
    const fallbackValue =
      typeof fallback === "number" && !Number.isNaN(fallback)
        ? fallback
        : Number.MIN_SAFE_INTEGER;
    const min = action.min ?? fallbackValue;
    const max = action.max ?? fallbackValue;
    return min <= max ? { min, max } : { min: max, max: min };
  }

  /**
   * Returns the next delta value for this action, or null when expansion is done.
   */
  private _computeDelta(
    values: number[],
    current: number,
    action: UpdateByDelta
  ): number | null {
    const min = action.min ?? Number.MIN_SAFE_INTEGER;
    const max = action.max ?? Number.MAX_SAFE_INTEGER;
    const lower = Math.min(min, max);
    const upper = Math.max(min, max);

    if (action.delta === "next" || action.delta === "prev") {
      const idx = values.findIndex(v => v === current);
      const step = action.delta === "next" ? 1 : -1;
      const candidate = values.at(idx + step);
      if (candidate === undefined) return null;
      if (candidate < lower || candidate > upper) return null;
      return candidate === current ? null : candidate;
    }

    if (Number.isNaN(action.delta) || action.delta === 0) {
      return null;
    }

    const candidate = current + action.delta;
    if (candidate < lower || candidate > upper) return null;
    return candidate === current ? null : candidate;
  }

  private async _computeContext(options: ProductOption[], isActive: () => void) {
    const nextState = this._createInitialState();

    for (const option of options) {
      isActive();

      this._updateMinMax(
        nextState.price,
        option.getPrice({ currency: false }),
        option.getPrice()
      );

      this._updateMinMax(
        nextState.price.monthly,
        option.getPrice({ monthly: true, currency: false }),
        option.getPrice({ monthly: true })
      );

      this._updateMinMax(
        nextState.discountedPrice,
        option.getDiscountedPrice({ currency: false }),
        option.getDiscountedPrice()
      );

      this._updateMinMax(
        nextState.discountedPrice.monthly,
        option.getDiscountedPrice({ monthly: true, currency: false }),
        option.getDiscountedPrice({ monthly: true })
      );

      this._updateMinMax(
        nextState.discount,
        option.getDiscount({ symbol: false }),
        option.getDiscount()
      );

      this._updateMinMax(
        nextState.discount.monthly,
        option.getDiscount({ monthly: true, symbol: false }),
        option.getDiscount({ monthly: true })
      );

      this._updateMinMax(
        nextState.discount.percentage,
        option.getDiscount({ percentage: true, symbol: false }),
        option.getDiscount({ percentage: true })
      );

      this._updateMinMax(
        nextState.discount.percentage.monthly,
        option.getDiscount({ monthly: true, percentage: true, symbol: false }),
        option.getDiscount({ monthly: true, percentage: true })
      );
    }

    this.host.state = nextState;
  }

  private _updateMinMax(range: MinMax, value: number, formatted: string) {
    if (value < range.min.value) {
      range.min.value = value;
      range.min.fmt = formatted;
    }
    if (value > range.max.value) {
      range.max.value = value;
      range.max.fmt = formatted;
    }
  }

  private _createInitialState(): stateContextType {
    const createRange = (): MinMax => ({
      min: { value: Number.MAX_SAFE_INTEGER, fmt: "" },
      max: { value: Number.MIN_SAFE_INTEGER, fmt: "" }
    });

    return {
      price: {
        ...createRange(),
        monthly: createRange()
      },
      discountedPrice: {
        ...createRange(),
        monthly: createRange()
      },
      discount: {
        ...createRange(),
        monthly   : createRange(),
        percentage: {
          ...createRange(),
          monthly: createRange()
        }
      }
    };
  }

  private async _getOptionByDimension(
    baseOpt: ProductOption,
    type: UpdateByDelta["type"],
    value: number
  ) {
    const devices = type === "devices" ? value : baseOpt.getDevices();
    const subscription = type === "subscription" ? value : baseOpt.getSubscription();
    return baseOpt.getOption({ devices, subscription });
  }

  private async _getOption({ id, campaign, devices, subscription }: Partial<Action>, bundle: ProductBundleOption[] = []) {
    if (id && devices !== undefined && subscription !== undefined) {
      const product = await this.host._store?.getProduct({ id, campaign });
      return await product?.getOption({ devices, subscription, bundle });
    }

    return null;
  }

  private async _applyBundles(baseOpt: ProductOption, bundles: Partial<Action>[]) {
    if (bundles.length === 0) {
      return [];
    }
    const result: ProductBundleOption[][] = [];

    const recurse = async (prefix: ProductBundleOption[], start: number) => {
      for (let i = start; i < bundles.length; i++) {
        const action = bundles[i];
        const bundleOption = await this._getOption({
          id          : action.id,
          campaign    : action.campaign,
          devices     : action.devices ?? baseOpt.getDevices(),
          subscription: action.subscription ?? baseOpt.getSubscription()
        });

        if (!bundleOption) {
          continue;
        }

        const next = prefix.concat({
          devicesFixed     : action.devices !== undefined,
          subscriptionFixed: action.subscription !== undefined,
          option           : bundleOption
        });

        result.push(next);
        await recurse(next, i + 1);
      }
    };

    const bundleOption = async (option: ProductOption, bundlesToApply: ProductBundleOption[] = []) => {
      for (const bundle of bundlesToApply) {
        const newOpt = await option.toogleBundle(bundle);
        if (newOpt) {
          option = newOpt;
        } else {
          return null;
        }
      }
      return option;
    };

    await recurse([], 0);

    return Promise.all(result.map(bundlesToApply => bundleOption(baseOpt, bundlesToApply)));
  }
}
